// apps/frontend/src/components/channels/ToolResultDisplay.tsx

import { useState } from 'react';

import { ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';

interface DisplayFields {
  image?: string;
  title: string;
  description?: string;
  price?: string;
  link?: string;
  badge?: string;
}

export interface DisplayConfig {
  type: 'carousel' | 'cards' | 'notice' | 'badges' | 'links';
  array_path?: string;
  fields: DisplayFields;
}

interface ToolResultDisplayProps {
  toolName: string;
  toolResult?: unknown;
  timestamp: string;
  displayConfig?: DisplayConfig;
}

const MAX_PREVIEW_LENGTH = 200;

// ── Path resolution utilities (mirrors widget template-resolver) ──

function getByPath(obj: unknown, path: string): unknown {
  let current: unknown = obj;
  for (const key of path.split('.')) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[key];
  }
  return current;
}

function normalizeValue(value: unknown): unknown {
  if (Array.isArray(value)) return value[0] ?? undefined;
  return value;
}

function resolveField(item: unknown, template: string): string {
  const hasPlaceholders = template.includes('{{');
  if (!hasPlaceholders) {
    const value = normalizeValue(getByPath(item, template));
    if (value !== undefined && value !== null) return String(value);
    return template.includes('.') ? '' : template;
  }
  return template.replace(/\{\{([^}]+)\}\}/g, (_match, path: string) => {
    const value = normalizeValue(getByPath(item, path.trim()));
    return value !== undefined && value !== null ? String(value) : '';
  });
}

interface ResolvedItem {
  image?: string;
  title: string;
  description?: string;
  price?: string;
  link?: string;
  badge?: string;
}

function resolveItems(toolResult: unknown, config: DisplayConfig): ResolvedItem[] {
  let rawItems: unknown[];

  if (config.array_path) {
    const value = getByPath(toolResult, config.array_path);
    if (Array.isArray(value)) rawItems = value;
    else if (value != null && typeof value === 'object') rawItems = [value];
    else return [];
  } else {
    if (Array.isArray(toolResult)) rawItems = toolResult;
    else if (toolResult != null && typeof toolResult === 'object') rawItems = [toolResult];
    else return [];
  }

  return rawItems.map((item) => {
    const resolved: ResolvedItem = { title: resolveField(item, config.fields.title) };
    if (config.fields.image) resolved.image = resolveField(item, config.fields.image);
    if (config.fields.description)
      resolved.description = resolveField(item, config.fields.description);
    if (config.fields.price) resolved.price = resolveField(item, config.fields.price);
    if (config.fields.link) resolved.link = resolveField(item, config.fields.link);
    if (config.fields.badge) resolved.badge = resolveField(item, config.fields.badge);
    return resolved;
  });
}

// ── Rich renderers ──

function CarouselDisplay({ items }: { items: ResolvedItem[] }) {
  return (
    <div className="flex gap-3 overflow-x-auto pb-2" style={{ scrollSnapType: 'x mandatory' }}>
      {items.map((item, i) => (
        <div
          key={i}
          className="flex w-52 min-w-[208px] shrink-0 flex-col overflow-hidden rounded-xl border bg-white shadow-sm transition-shadow hover:shadow-md dark:bg-slate-900"
          style={{ scrollSnapAlign: 'start' }}
        >
          {item.image && (
            <div className="relative h-32 w-full overflow-hidden bg-slate-100">
              <img src={item.image} alt={item.title} className="h-full w-full object-cover" />
              {item.price && (
                <span className="absolute bottom-2 left-2 rounded-md bg-black/70 px-2 py-0.5 text-xs font-bold text-white backdrop-blur-sm">
                  {item.price}
                </span>
              )}
            </div>
          )}
          <div className="flex flex-1 flex-col gap-1 p-3">
            <span className="text-sm leading-tight font-semibold">{item.title}</span>
            {item.description && (
              <span className="text-xs text-slate-500 dark:text-slate-400">{item.description}</span>
            )}
            {item.price && !item.image && (
              <span className="text-sm font-bold text-blue-600">{item.price}</span>
            )}
            {item.link && (
              <a
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-auto inline-flex items-center gap-1 pt-1 text-xs font-semibold text-blue-600 hover:text-blue-800"
              >
                View Details <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function CardsDisplay({ items }: { items: ResolvedItem[] }) {
  return (
    <div className="flex flex-col gap-2">
      {items.map((item, i) => (
        <div
          key={i}
          className="flex gap-3 rounded-xl border bg-white p-3 shadow-sm transition-shadow hover:shadow-md dark:bg-slate-900"
        >
          {item.image && (
            <img
              src={item.image}
              alt={item.title}
              className="h-16 w-16 shrink-0 rounded-lg object-cover"
            />
          )}
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="text-sm font-semibold">{item.title}</span>
            {item.description && <span className="text-xs text-slate-500">{item.description}</span>}
            <div className="mt-1 flex items-center justify-between">
              {item.price && <span className="text-sm font-bold text-blue-600">{item.price}</span>}
              {item.link && (
                <a
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-semibold text-blue-600 hover:text-blue-800"
                >
                  View →
                </a>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function NoticeDisplay({ items }: { items: ResolvedItem[] }) {
  const item = items[0];
  if (!item) return null;
  return (
    <div className="rounded-r-lg border-l-4 border-blue-500 bg-blue-50 p-3 dark:bg-blue-950">
      <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{item.title}</p>
      {item.description && (
        <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">{item.description}</p>
      )}
      {item.link && (
        <a
          href={item.link}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-1 inline-block text-xs font-medium text-blue-600 underline"
        >
          Learn more
        </a>
      )}
    </div>
  );
}

function BadgesDisplay({ items }: { items: ResolvedItem[] }) {
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((item, i) => {
        const Tag = item.link ? 'a' : 'span';
        const linkProps = item.link
          ? { href: item.link, target: '_blank' as const, rel: 'noopener noreferrer' }
          : {};
        return (
          <Tag
            key={i}
            {...linkProps}
            className="inline-block rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 no-underline dark:border-blue-800 dark:bg-blue-950 dark:text-blue-300"
          >
            {item.badge || item.title}
          </Tag>
        );
      })}
    </div>
  );
}

function LinksDisplay({ items }: { items: ResolvedItem[] }) {
  return (
    <div className="flex flex-col gap-1.5">
      {items.map((item, i) => (
        <div key={i} className="rounded-lg bg-slate-50 p-2.5 dark:bg-slate-900">
          <a
            href={item.link || '#'}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-blue-600 no-underline hover:text-blue-800"
          >
            {item.title}
          </a>
          {item.description && <p className="mt-0.5 text-xs text-slate-500">{item.description}</p>}
        </div>
      ))}
    </div>
  );
}

const RENDERERS: Record<DisplayConfig['type'], React.FC<{ items: ResolvedItem[] }>> = {
  carousel: CarouselDisplay,
  cards: CardsDisplay,
  notice: NoticeDisplay,
  badges: BadgesDisplay,
  links: LinksDisplay,
};

// ── Raw JSON fallback ──

function RawJsonDisplay({ toolResult }: { toolResult: unknown }) {
  const [expanded, setExpanded] = useState(false);
  const formatted = formatResult(toolResult);
  const needsTruncation = formatted.length > MAX_PREVIEW_LENGTH;
  const preview = needsTruncation ? `${formatted.slice(0, MAX_PREVIEW_LENGTH)}...` : formatted;

  if (toolResult === undefined || toolResult === null) return null;

  return (
    <div>
      <pre className="mt-1 overflow-x-auto text-xs break-words whitespace-pre-wrap text-slate-700 dark:text-slate-300">
        {expanded ? formatted : preview}
      </pre>
      {needsTruncation ? (
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="mt-1 flex items-center gap-1 text-xs font-medium text-amber-700 hover:text-amber-900 dark:text-amber-400 dark:hover:text-amber-200"
        >
          {expanded ? (
            <>
              <ChevronDown className="h-3 w-3" /> Show less
            </>
          ) : (
            <>
              <ChevronRight className="h-3 w-3" /> Show more
            </>
          )}
        </button>
      ) : null}
    </div>
  );
}

function formatResult(value: unknown): string {
  if (value === undefined || value === null) return 'No result';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

// ── Main component ──

export const ToolResultDisplay = ({
  toolName,
  toolResult,
  timestamp,
  displayConfig,
}: ToolResultDisplayProps) => {
  // Try rich rendering if display config exists
  if (displayConfig && toolResult != null) {
    const items = resolveItems(toolResult, displayConfig);
    if (items.length > 0) {
      const Renderer = RENDERERS[displayConfig.type];
      if (Renderer) {
        return (
          <div className="rounded-lg border bg-white p-3 text-sm dark:bg-slate-950">
            <div className="mb-2 flex items-center gap-2">
              <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-blue-900 uppercase dark:bg-blue-800 dark:text-blue-100">
                {displayConfig.type}
              </span>
              <span className="font-medium">{toolName}</span>
              <span className="text-muted-foreground ml-auto text-[10px]">
                {new Date(timestamp).toLocaleTimeString()}
              </span>
            </div>
            <Renderer items={items} />
          </div>
        );
      }
    }
  }

  // Fallback to raw JSON display
  return (
    <div className="rounded border border-amber-200 bg-amber-50 p-2 text-sm dark:border-amber-800 dark:bg-amber-950">
      <div className="mb-1 flex items-center gap-2">
        <span className="rounded bg-amber-200 px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-amber-900 uppercase dark:bg-amber-800 dark:text-amber-100">
          Tool
        </span>
        <span className="font-medium">{toolName}</span>
        <span className="text-muted-foreground ml-auto text-[10px]">
          {new Date(timestamp).toLocaleTimeString()}
        </span>
      </div>
      <RawJsonDisplay toolResult={toolResult} />
    </div>
  );
};
